import {AppBar, Toolbar} from '@mui/material';
import classnames from 'classnames';
import {map, isEmpty} from 'lodash';
import React, {useEffect, useState, ReactNode} from 'react';
import {Link, useLocation} from 'react-router-dom';
import qs from 'qs';

import {useMediaQuery} from 'src/hooks/useMediaQuery';
import Icon from 'shared/components/icon';
import UnstyledButton from 'src/components/unstyled-button';
import useFeatureFlags from 'src/hooks/useFeatureFlags';

import NavbarAvatarMenu from './NavbarAvatarMenu';
import {NavbarButtonProps} from './NavbarButton';
import NavbarCTAButton, {CTAButtonProps} from './NavbarCTAButton';
import NavbarLogo from './NavbarLogo';
import {NavbarMenuProps} from './NavbarMenu/NavbarMenu';
import NavbarMenuList from './NavbarMenuList';
import MobileNavbar from './mobile-navbar/MobileNavbar';
import NavbarDrawer from './navbar-drawer';

import 'shared/components/navbar/navbar-logo.scss';
import styles from './styles.module.scss';
import {useBoolean} from '../../hooks/useBoolean';
import LeadGenerationLogo from './lead-generation-logo/lead-genreation-logo';

export interface NavbarUserInfo {
  firstName?: string;
  lastName?: string;
  image?: string;
}

export interface NavbarProps {
  routed?: boolean | undefined;
  userInfo?: NavbarUserInfo;
  menus: (NavbarMenuProps | NavbarButtonProps)[];
  SearchBar?: ReactNode | null;
  ctaButtons?: CTAButtonProps[];
  featureFlags?: string[];
  cobrandedLogo?: string | undefined;
  showNotificationBell?: boolean | undefined;
  avatarMenuItems?: NavbarButtonProps[];
  homePath?: string;
  experienceEverywhere?: boolean;
  isCollegePortal?: boolean;
}

const Navbar = ({
  menus,
  avatarMenuItems,
  SearchBar,
  ctaButtons,
  routed = true,
  userInfo,
  cobrandedLogo,
  showNotificationBell,
  homePath,
  experienceEverywhere,
  isCollegePortal = false,
}: NavbarProps) => {
  const mq = useMediaQuery();
  const {search} = useLocation();
  const [open, setOpen] = useState(false);
  const headerKitLoading = useBoolean(true);
  const {showSearch} = qs.parse(search.substr(1));
  const toggleDrawer = () => setOpen(!open);
  const desktopVersion = mq.MD_AND_UP || experienceEverywhere;
  const flags = useFeatureFlags(['leadGenerationLogoFlag']);
  const flagsLoaded = !isEmpty(flags);
  const notificationClass = (): string => {
    const active = window.location.pathname === '/notification-center';

    return classnames('complete-profile-experiment nav-notification-center', {active});
  };

  useEffect(() => {
    if (!experienceEverywhere) {
      return;
    }

    let count = 0;

    const checkHeaderKit = () => {
      count += 1;

      if (document.querySelectorAll('#header-kit button').length >= 3 || count > 60) {
        headerKitLoading.setFalse();
      } else {
        setTimeout(checkHeaderKit, 100);
      }
    };

    checkHeaderKit();
  }, []);

  return (
    <div
      className={classnames('no-top-padding sticky-nav-header-top', {
        '--experience-everywhere': experienceEverywhere,
      })}
      data-testid="basic-navbar-element"
    >
      {desktopVersion ? (
        <nav className={classnames('navbar-base', 'toolbar')}>
          <div className="container">
            <AppBar
              position="static"
              color="inherit"
              elevation={0}
              className={classnames(styles.root, {
                [styles.experienceEverywhere]: experienceEverywhere,
              })}
            >
              <Toolbar disableGutters>
                {experienceEverywhere && (
                  <UnstyledButton
                    className={styles.barsButton}
                    aria-label="Toggle navigation menu"
                    onClick={toggleDrawer}
                  >
                    <Icon iconType="bars-r" />
                  </UnstyledButton>
                )}
                {isCollegePortal && !flagsLoaded ? null : flags.leadGenerationLogoFlag &&
                  isCollegePortal ? (
                  <LeadGenerationLogo experienceEverywhere={experienceEverywhere} />
                ) : (
                  <NavbarLogo
                    routed={routed}
                    homePath={homePath}
                    experienceEverywhere={experienceEverywhere}
                  />
                )}

                {experienceEverywhere ? (
                  <>
                    <div className={styles.spacer}></div>
                    {showSearch && !!SearchBar && (
                      <div className={styles.searchExperienceEverywhere}>{SearchBar}</div>
                    )}
                    <div
                      id="header-kit"
                      className={classnames({[styles.hidden]: headerKitLoading.value})}
                    />
                    <div
                      className={classnames(styles.headerKitLoading, {
                        [styles.hidden]: !headerKitLoading.value,
                      })}
                    >
                      <Icon iconType="spinner" className="rm-spin" />
                    </div>
                  </>
                ) : (
                  <>
                    <NavbarMenuList menus={menus} routed={routed} />
                    <div className={styles.spacer}></div>
                    {!!SearchBar && <div className={styles.search}>{SearchBar}</div>}
                    {ctaButtons &&
                      map(ctaButtons, (button) => (
                        <NavbarCTAButton key={button.title} {...button} />
                      ))}
                    {showNotificationBell && (
                      <Link
                        className={notificationClass()}
                        to="/notification-center-refresh"
                        aria-label="What's New"
                        role="button"
                      >
                        <Icon className="notification-bell-icon" iconType="bell" />
                      </Link>
                    )}
                    {cobrandedLogo && (
                      <img className="navbar-college-logo" src={cobrandedLogo} alt="College logo" />
                    )}
                  </>
                )}
                {avatarMenuItems && (
                  <NavbarAvatarMenu
                    userInfo={userInfo}
                    menuItems={avatarMenuItems}
                    experienceEverywhere={experienceEverywhere}
                  />
                )}
              </Toolbar>
            </AppBar>
          </div>
        </nav>
      ) : (
        <MobileNavbar
          routed={routed}
          onDrawerToggle={toggleDrawer}
          isCollegePortal={isCollegePortal}
        />
      )}
      {(experienceEverywhere || mq.SM_AND_DOWN) && (
        <NavbarDrawer
          anchor={desktopVersion ? 'left' : 'right'}
          open={open}
          menus={menus}
          ctaButtons={ctaButtons}
          avatarMenuItems={avatarMenuItems}
          routed={routed}
          setOpen={setOpen}
        />
      )}
    </div>
  );
};

export default Navbar;
